/**
 * Country flag + label helpers. Renders the flag as an `<img>` from flagcdn
 * (SVG, cached) rather than a Unicode emoji — emoji flags render
 * inconsistently across OSes (Windows hides them entirely, Linux falls back
 * to two-letter glyphs) and are the wrong size for inline icon use.
 *
 * Usage:
 *   <CountryFlag code="CAN" size={16} />
 *   countryLabel("CAN") // "Canada"
 *   countryIso2("CAN")  // "ca"
 */

export const COUNTRY_LABELS: Record<string, string> = {
  CAN: "Canada",
  USA: "United States",
  GBR: "United Kingdom",
  IND: "India",
  AUS: "Australia",
  NZL: "New Zealand",
  TUR: "Turkey",
  KHM: "Cambodia",
  SGP: "Singapore",
  MUS: "Mauritius",
};

const COUNTRY_ISO2: Record<string, string> = {
  CAN: "ca",
  USA: "us",
  GBR: "gb",
  IND: "in",
  AUS: "au",
  NZL: "nz",
  TUR: "tr",
  KHM: "kh",
  SGP: "sg",
  MUS: "mu",
};

export function countryLabel(code: string): string {
  return COUNTRY_LABELS[code] ?? code;
}

export function countryIso2(code: string): string {
  return COUNTRY_ISO2[code] ?? code.slice(0, 2).toLowerCase();
}

export function CountryFlag({ code, size = 20 }: { code: string; size?: number }) {
  const iso = countryIso2(code);
  const label = countryLabel(code);
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#f0f0ee] dark:bg-[#252d3a]"
      style={{ width: size, height: size }}
      title={label}
    >
      <img
        src={`https://flagcdn.com/${iso}.svg`}
        alt={label}
        width={size}
        height={size}
        className="h-full w-full object-cover"
        loading="lazy"
        onError={(e) => {
          const el = e.currentTarget;
          el.style.display = "none";
        }}
      />
    </span>
  );
}
